import type { GameSettings, SaveData, SongBackground, SongProgress } from '@/types';
import { CATS } from '@/data/cats';
import { BACKGROUNDS } from '@/data/backgrounds';
import { FOODS } from '@/data/foods';
import { STORAGE_KEY } from '@/game/config/GameConfig';

export const DEFAULT_SETTINGS: GameSettings = {
  musicVolume: 0.8,
  sfxVolume: 1,
  vibration: true,
  reducedEffects: false,
  audioOffsetMs: 0,
};

function createDefaultSaveData(): SaveData {
  const defaultCats = CATS.filter((c) => c.unlockedByDefault).map((c) => c.id);
  const defaultBackgrounds = BACKGROUNDS.filter((b) => b.unlockedByDefault).map((b) => b.id);
  return {
    coins: 100,
    unlockedCatIds: defaultCats,
    selectedLeftCatId: defaultCats[0],
    selectedRightCatId: defaultCats[1] ?? defaultCats[0],
    unlockedBackgroundIds: defaultBackgrounds,
    selectedBackgroundId: null,
    enabledFoodIds: FOODS.map((f) => f.id),
    songProgress: {},
    settings: { ...DEFAULT_SETTINGS },
  };
}

/** Abstraction over where save data physically lives. localStorage today; swapping to
 * a remote backend later means implementing this interface, not touching callers. */
export interface SaveStore {
  load(): SaveData | null;
  save(data: SaveData): void;
}

export class LocalStorageSaveStore implements SaveStore {
  load(): SaveData | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as SaveData;
    } catch {
      return null;
    }
  }

  save(data: SaveData): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // localStorage unavailable (private browsing, quota) -- fail silently, gameplay continues.
    }
  }
}

/** Facade over persisted player state: coins, cosmetic unlocks/selection, song
 * high scores, and settings. Every mutation immediately persists via the SaveStore. */
export class SaveManager {
  private data: SaveData;

  constructor(private store: SaveStore = new LocalStorageSaveStore()) {
    const loaded = this.store.load();
    this.data = loaded ? mergeWithDefaults(loaded) : createDefaultSaveData();
  }

  getData(): Readonly<SaveData> {
    return this.data;
  }

  getSettings(): GameSettings {
    return this.data.settings;
  }

  updateSettings(partial: Partial<GameSettings>): void {
    this.data.settings = { ...this.data.settings, ...partial };
    this.persist();
  }

  addCoins(amount: number): void {
    this.data.coins += amount;
    this.persist();
  }

  spendCoins(amount: number): boolean {
    if (this.data.coins < amount) return false;
    this.data.coins -= amount;
    this.persist();
    return true;
  }

  unlockCat(catId: string): void {
    if (!this.data.unlockedCatIds.includes(catId)) {
      this.data.unlockedCatIds.push(catId);
      this.persist();
    }
  }

  isCatUnlocked(catId: string): boolean {
    return this.data.unlockedCatIds.includes(catId);
  }

  setSelectedCat(lane: 'left' | 'right', catId: string): void {
    if (lane === 'left') this.data.selectedLeftCatId = catId;
    else this.data.selectedRightCatId = catId;
    this.persist();
  }

  unlockBackground(backgroundId: string): void {
    if (!this.data.unlockedBackgroundIds.includes(backgroundId)) {
      this.data.unlockedBackgroundIds.push(backgroundId);
      this.persist();
    }
  }

  isBackgroundUnlocked(backgroundId: string): boolean {
    return this.data.unlockedBackgroundIds.includes(backgroundId);
  }

  /** null means "use each song's own default background". */
  setSelectedBackground(backgroundId: SongBackground | null): void {
    this.data.selectedBackgroundId = backgroundId;
    this.persist();
  }

  getSelectedBackgroundId(): SongBackground | null {
    return this.data.selectedBackgroundId;
  }

  getEnabledFoodIds(): string[] {
    return this.data.enabledFoodIds;
  }

  isFoodEnabled(foodId: string): boolean {
    return this.data.enabledFoodIds.includes(foodId);
  }

  /** Toggles a treat on/off. Refuses to disable the last remaining enabled treat --
   * gameplay always needs at least one -- and returns whether the toggle applied. */
  setFoodEnabled(foodId: string, enabled: boolean): boolean {
    const has = this.data.enabledFoodIds.includes(foodId);
    if (enabled === has) return true;
    if (!enabled && this.data.enabledFoodIds.length <= 1) return false;
    this.data.enabledFoodIds = enabled
      ? [...this.data.enabledFoodIds, foodId]
      : this.data.enabledFoodIds.filter((id) => id !== foodId);
    this.persist();
    return true;
  }

  getSongProgress(songId: string): SongProgress {
    return this.data.songProgress[songId] ?? { highScore: 0, stars: 0 };
  }

  /** Records a completed play, returning whether it beat the previous high score. */
  recordSongResult(songId: string, score: number, stars: 1 | 2 | 3): boolean {
    const existing = this.getSongProgress(songId);
    const isNewHighScore = score > existing.highScore;
    this.data.songProgress[songId] = {
      highScore: Math.max(existing.highScore, score),
      stars: Math.max(existing.stars, stars) as 0 | 1 | 2 | 3,
    };
    this.persist();
    return isNewHighScore;
  }

  resetSaveData(): void {
    this.data = createDefaultSaveData();
    this.persist();
  }

  private persist(): void {
    this.store.save(this.data);
  }
}

function mergeWithDefaults(loaded: SaveData): SaveData {
  const defaults = createDefaultSaveData();
  return {
    ...defaults,
    ...loaded,
    settings: { ...defaults.settings, ...loaded.settings },
  };
}
